import { useMemo } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import { weaponInstanceKey, type EncounterState, type HeldAction, type Side, type WeaponProfile } from "@lancer-vtt/domain";
import { MECH_ACTIONS, isAttackShapedHeld } from "@lancer-vtt/rules";
import type { ActionDraft } from "../combatInspector";
import { rankAdvisorPlans, planToArmedAction } from "../advisorPanel";
import type { AutoplayBotControls } from "../autoplayBot";
import { cellLabel } from "../cellLabel";

/**
 * The **PREPARED hold telegraph** — the unit sheet's hold banner, under the header ({@link UnitSheet}'s
 * `hold` slot). Four mounts: {@link MechInspectorBody}, {@link NpcInspectorBody},
 * {@link PilotInspectorBody} and the read-only `PlayerNpcInspectorBody`.
 *
 * This panel is the ARMED half only. A hold is armed from {@link PrepareChooser} on the mech and NPC
 * QUICK columns (`projects/prepare-hold-browser-arming.md` Phase 1) or from the CLI's `prepare` verb;
 * either way it lands in the same synced `activation.heldActions` and renders here. A pilot's hold
 * can only come from the CLI — that row is `engine-pending` for a rules reason (D3), not a missing
 * chooser — and it still renders and releases through this panel.
 *
 * The badge + trigger text render for **every client** unconditionally: a held action is synced
 * top-level `ActivationState` (survives reconnect), so it telegraphs table-wide for free, exactly
 * like the mid-attack lock. RELEASE NOW / DROP render only for the owner/GM (`canControl`, the
 * same gate the rest of the surface uses) — purely an affordance, since the server independently
 * enforces both (`canIssueEncounter`/`resolveRelease` keyed on the preparer for release,
 * `drop_prepare`'s own reducer case for drop). RELEASE NOW arms the board-wide `release`
 * {@link ActionDraft} (no in-panel chooser — the readied verb/weapon are already pinned in the
 * held record); the next token click fires `buildReleaseIntent` from the host's `fireAt`
 * (`CombatApp.tsx`), which derives the weapon/tech pair off `held` and names `releasedBy`. DROP
 * sends `drop_prepare` directly (no board interaction — it's a self-only, no-target command).
 *
 * **It names what is held** (tracker D9): a verb/weapon line beside the badge, so a player who
 * armed "Skirmish · Assault Rifle" reads what fires instead of inferring it from the trigger text.
 * See {@link heldSummaryLine} for the resolution.
 */
export function PreparedHoldPanel({
  held,
  weapons,
  releaseArmed,
  canControl,
  onArmRelease,
  onDrop,
}: {
  held: HeldAction;
  /**
   * The preparer's frozen weapons (`c.base.weapons`) — the ONLY thing {@link heldSummaryLine}
   * resolves `held.weapon` against, since that field is a {@link weaponInstanceKey}, not a
   * compendium id. Pass `[]` when the body has none frozen; the line then names the verb alone.
   */
  weapons: readonly WeaponProfile[];
  releaseArmed: boolean;
  canControl: boolean;
  onArmRelease: () => void;
  onDrop: () => void;
}) {
  // A hold whose readied id is not attack-shaped (a granted capped action armed from the CLI, or a
  // Scan/Lock-On/Bolster `quick_tech`) has no release path THROUGH THIS BUTTON: `buildReleaseIntent`
  // builds a `request_attack` and the room's `resolveRelease` refuses it on the id-match. Phase 3 of
  // `projects/prepare-hold-browser-arming.md` (D6) builds the `request_activate` release that covers
  // those; until it lands the button says so instead of sending a command that is certain to bounce.
  // DROP stays live either way — `drop_prepare` is id-agnostic.
  const releasable = isAttackShapedHeld(held);
  return (
    <div className="unit-sheet-hold">
      <span className="unit-sheet-hold-badge">PREPARED</span>
      <span className="unit-sheet-hold-verb">{heldSummaryLine(held, weapons)}</span>
      <span className="unit-sheet-hold-trigger" title={held.triggerText}>
        “{held.triggerText}”
      </span>
      {canControl && (
        <div className="unit-sheet-hold-actions">
          <button
            type="button"
            className="unit-sheet-hold-btn is-release"
            disabled={!releasable}
            title={
              releasable
                ? "Release the held action now — pick a target on the board"
                : "This hold can only be released from the CLI (lvtt encounter release) — browser release for granted actions isn't built yet"
            }
            onClick={onArmRelease}
          >
            {releaseArmed ? "PICK TARGET…" : "RELEASE NOW"}
          </button>
          <button
            type="button"
            className="unit-sheet-hold-btn"
            title="Voluntarily drop the hold — regain reactions, no refund"
            onClick={onDrop}
          >
            DROP
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The **held-action line** (tracker D9) — "Skirmish · Assault Rifle", "Ram", "Quick Tech".
 *
 * The verb's display name is looked up in {@link MECH_ACTIONS} rather than hand-written. Every
 * `PREPARABLE_ACTION_IDS` member is a row there, and `NPC_ACTIONS`/`PILOT_ACTIONS` share those ids
 * AND their `name` strings byte-for-byte, so one lookup covers all three bodies; an id with no row
 * (a granted capped action, which a CLI-armed hold can carry) falls back to the raw id rather than
 * rendering blank.
 *
 * The weapon is resolved off the preparer's frozen `base.weapons` by {@link weaponInstanceKey},
 * because `held.weapon` is an INSTANCE key (`"0:0"`, `"npc:1"`, `"manual:0"`) and not a compendium
 * id — two mounts of the same weapon differ only there. A `held.weapon` that resolves to nothing
 * renders as the verb alone, which is the right answer for the synthetic `act_*` weapons a Quick
 * Tech (`"act_invade"`) hold carries: they name no card on this unit.
 */
export function heldSummaryLine(held: HeldAction, weapons: readonly WeaponProfile[]): string {
  const verb = MECH_ACTIONS.find((a) => a.id === held.readiedActionId)?.name ?? held.readiedActionId;
  const weapon =
    held.weapon === undefined ? undefined : weapons.find((w) => weaponInstanceKey(w) === held.weapon);
  return weapon ? `${verb} · ${weapon.name}` : verb;
}

/**
 * The **advisor panel** — surfaces
 * `rankAdvisorPlans`'s top-k ranked plans for the combatant that IS `state.activation.turn.actor`.
 * That's the SAME v1 scope `rankAdvisorPlans`/`generateMacroMoves` already enforce (`advisorPanel.ts`'s
 * header) — a not-yet-activated unit ranks to `[]`, so this simply renders nothing then; callers still
 * gate on `isGm && isActiveActor` so the empty-render path is never even reached for a unit whose turn
 * this isn't. Shared by {@link NpcInspectorBody} and {@link MechInspectorBody} (both call it
 * identically) — the `PreparedHoldPanel` precedent for a two-body-shared control-surface component.
 *
 * **GM-only, and collapsed until asked for.** The gate is `isGm`, NOT the `canControl` every *other* own-unit affordance in this
 * panel uses: a player commanding their own mech no longer sees the advisor at all. `canControl` is
 * deliberately absent from the gate rather than `&&`-ed in — GM ⇒ `canControl` is always true (see
 * {@link InspectorBody}'s own `canControl` doc), so writing both would imply a narrowing that isn't
 * there. Reads-only either way; the server was never the thing gating this (nothing here dispatches).
 *
 * The panel itself is a **native uncontrolled `<details>`**, closed on mount — the same idiom (and
 * the same reasoning) as the per-row terms breakdown below: `<details>` already defaults closed, so
 * no `open` prop, no `useState`, and no preferences mechanism; an expanded panel stays expanded
 * across the re-render every server tick causes mid-combat. Collapsing it costs the advisor its
 * always-on screen real estate in the busiest panel on the board and makes it a thing the GM opens
 * when they want a second opinion.
 *
 * **Arm-on-click, not autopilot** (the FORGE "advisor not editor" doctrine):
 * clicking an attack-bearing plan's row calls {@link planToArmedAction} and threads the result
 * straight into the SAME `setAim` the action grid uses — a fixed-target Skirmish draft. That, in
 * turn, is exactly what makes {@link SkirmishChooser}'s existing `fixedTargetId` FIRE button render
 * (the `startOverwatchTake` precedent, `CombatApp.tsx`, see that button's own comment below, updated
 * to name this as its second source): the human reviews the armed weapon/target and presses FIRE
 * themselves — nothing here dispatches directly. A move-only plan (`move.attack === null`) has
 * nothing to arm (`planToArmedAction` returns `aim: null`), so its row renders informational, not
 * clickable.
 *
 * **The move leg is NOT sent and has no board ghost — a documented v1 limitation.** The advisor is
 * read-only compute with no mutation path, so a plan that moves first never dispatches
 * `move.moveIntent`. Investigated whether the board already exposes a non-mutating "preview a
 * destination" affordance to reuse for this: the only ghost machinery on the board
 * (`CombatStage.tsx`'s `createGhost`/`snapGhost`/`clearGhost`) is wired to a
 * LIVE pointer drag, not addressable from a static cell key on a panel click — reusing it here would
 * mean inventing a new non-drag trigger for it, exactly the "don't invent a new board-overlay
 * system" this deliberately avoids. So the destination instead renders as plain text in
 * the row (`moveDestinationKey`, `world.key`-encoded) — the GM drives the move themselves (drag the
 * token, or `lvtt encounter move`) before arming/firing the attack leg.
 */
export function AdvisorPanel({
  combatantId,
  state,
  compendium,
  setAim,
}: {
  combatantId: string;
  state: EncounterState;
  compendium: Compendium;
  setAim: (a: ActionDraft | null) => void;
}) {
  const rows = useMemo(
    () => rankAdvisorPlans({ state, actorId: combatantId, compendium }),
    [state, combatantId, compendium],
  );
  if (rows.length === 0) return null;

  return (
    <details className="unit-sheet-advisor">
      {/* The summary IS the panel's accessible name — no `role`/`aria-label` override, which would
          shadow the disclosure semantics a native `<details>` gives for free. */}
      <summary className="unit-sheet-advisor-summary">
        <span className="unit-sheet-label">Advisor</span>
      </summary>
      <ul className="unit-sheet-advisor-list">
        {rows.map((row) => {
          const move = row.ranked.move;
          // Named the way the BOARD's ruler names it (`cellLabel` → `G5`), not the raw `world.key`
          // pair — the GM reads this note off the same grid they're looking at.
          const moveNote = move.moved ? `move to ${cellLabel(state, move.destination)} first` : null;
          if (move.attack === null) {
            // A move-only/hold plan has nothing to arm (`planToArmedAction` returns `aim: null`) —
            // an informational row, not a button; there's nothing a click here would do.
            return (
              <li key={row.id} className="unit-sheet-advisor-row unit-sheet-advisor-row-inert">
                <span className="unit-sheet-advisor-reason">{row.reasoning}</span>
                {moveNote && <span className="unit-sheet-advisor-move">{moveNote}</span>}
              </li>
            );
          }
          return (
            <li key={row.id} className="unit-sheet-advisor-row">
              <button
                type="button"
                className="unit-sheet-advisor-plan"
                title="Arm this plan's attack — review the weapon/target, then press FIRE"
                onClick={() => {
                  const armed = planToArmedAction(state, move);
                  if (armed.aim) setAim(armed.aim);
                }}
              >
                <span className="unit-sheet-advisor-reason">{row.reasoning}</span>
                {moveNote && (
                  <span
                    className="unit-sheet-advisor-move"
                    title="The advisor is read-only — this move isn't sent; drive it yourself, then arm/fire"
                  >
                    {moveNote}
                  </span>
                )}
              </button>
              {/* Per-term breakdown, "collapsed by default" (`row.collapsed`, always `true` today —
                  `advisorPanel.ts`'s own data field, not UI state). Deliberately UNCONTROLLED (no
                  `open` prop): `<details>` already defaults closed, matching `row.collapsed`, and
                  stays freely user-toggleable across re-renders — `state` changes on nearly every
                  server tick during combat, so a CONTROLLED `open={!row.collapsed}` would force-reset
                  an already-expanded breakdown shut the next time any encounter state changed. */}
              {row.terms.length > 0 && (
                <details className="unit-sheet-advisor-terms">
                  <summary>terms</summary>
                  <ul>
                    {row.terms.map((t) => (
                      <li key={t.name}>
                        <span className="unit-sheet-advisor-term-name">{t.name}</span>
                        <span className="unit-sheet-advisor-term-val">{t.contribution.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

/**
 * The **autoplay controls** (-§4) — GM-only
 * "play this activation" / "play this side" buttons, beside ACTIVATE TURN / END TURN. Pure
 * presentation over the LIFTED `AutoplayBotControls` (`../autoplayBot.ts`'s `useAutoplayBot`, ONE
 * instance per client, built in `CombatApp.tsx`) — never re-implements the run/halt/resume state
 * machine here, just renders it; never re-implements `autoplayActivation`/`autoplaySide` either
 * (`bot.playActivation`/`bot.playSide` compose them UNCHANGED). Shared by {@link NpcInspectorBody}
 * and {@link MechInspectorBody} — the `AdvisorPanel`/`PreparedHoldPanel` precedent for a
 * two-body-shared control component.
 *
 * **GM-only, on a GM-controlled unit** — the caller gates the whole render
 * on `canControl && isGm`; player-self-autoplay is a named deferred follow-on, not built here.
 * "Play this activation" is additionally disabled unless `isActiveActor` (it plays THIS unit's
 * CURRENT turn — nothing to play otherwise, mirroring `canActivate`'s own disabled logic just
 * above it in both bodies); "play this side" has no such requirement — it drives whichever unit
 * the side's own `pickActivation` argmax picks next, which may not be this one.
 *
 * **The halt/run banner is deliberately global, not per-panel** (`useAutoplayBot`'s own "one bot
 * run at a time per client" design): every gated panel renders the SAME
 * shared `bot.state`, so a run started from one NPC's panel stays visible — and resumable — from
 * any other GM-controlled panel too, not just the one that started it. A silent halt with no
 * affordance is the defect this exists to avoid; RESUME/DISMISS are
 * always one click away wherever the GM is looking. RESUME is nothing more than re-invoking the
 * SAME operation from the current authoritative state (`autoplayBot.ts`'s header) — no new
 * "continue from here" machinery.
 */
export function AutoplayControls({
  bot,
  unitId,
  unitLabel,
  side,
  isActiveActor,
}: {
  bot: AutoplayBotControls;
  unitId: string;
  unitLabel: string;
  side: Side;
  isActiveActor: boolean;
}) {
  const sideLabel = side === "pc" ? "LANCERS" : "HOSTILES";
  return (
    <div className="unit-sheet-autoplay" role="group" aria-label="autoplay">
      <span className="unit-sheet-label">Autoplay</span>
      <div className="unit-sheet-autoplay-btns">
        <button
          type="button"
          className="unit-sheet-autoplay-btn"
          disabled={bot.busy || !isActiveActor}
          title={isActiveActor ? `Play ${unitLabel}'s activation to end_turn` : "Not this unit's turn"}
          aria-label="play this activation"
          onClick={() => bot.playActivation(unitId, unitLabel)}
        >
          PLAY ACTIVATION
        </button>
        <button
          type="button"
          className="unit-sheet-autoplay-btn"
          disabled={bot.busy}
          title={`Play the ${sideLabel} side's activations`}
          aria-label="play this side"
          onClick={() => bot.playSide(side, sideLabel)}
        >
          PLAY SIDE
        </button>
      </div>
      {bot.state.phase === "running" && (
        <div className="unit-sheet-autoplay-status is-running">RUNNING — {bot.state.label}…</div>
      )}
      {bot.state.phase === "halted" && (
        <div className="unit-sheet-autoplay-status is-halted">
          <span>
            HALTED ({bot.state.label}) — {bot.state.reason}
          </span>
          <div className="unit-sheet-autoplay-status-actions">
            {bot.state.resumable && (
              <button type="button" className="unit-sheet-autoplay-status-btn is-resume" onClick={bot.resume}>
                RESUME
              </button>
            )}
            <button type="button" className="unit-sheet-autoplay-status-btn" onClick={bot.dismiss}>
              DISMISS
            </button>
          </div>
        </div>
      )}
      {bot.state.phase === "failed" && (
        <div className="unit-sheet-autoplay-status is-failed">
          <span>
            FAILED ({bot.state.label}) — {bot.state.reason}
          </span>
          <div className="unit-sheet-autoplay-status-actions">
            <button type="button" className="unit-sheet-autoplay-status-btn" onClick={bot.dismiss}>
              DISMISS
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
